import type {
  DataProvider,
  DashboardData,
  BookingsData,
  AnalyticsData,
  PayoutData
} from "./types";
import type { Studio, Room, Profile } from "@/types/database";
import type { BookingWithDetails } from "@/components/bookings/bookings-data-table";
import type { AvailabilitySnapshot } from "@/lib/analytics-mock-data";
import { supabase } from "@/lib/supabase";
import { API_URL } from "@/lib/api/api-client";
import { format, subDays } from "date-fns";

export class SupabaseDataProvider implements DataProvider {
  async getDashboardData(userId: string): Promise<DashboardData> {
    // Load studio - get the most recent one if multiple exist
    const { data: studios, error: studioError } = await supabase
      .from("studios")
      .select("*")
      .eq("owner_id", userId)
      .order("created_at", { ascending: false })
      .limit(1);

    const studioData = studios?.[0];

    if (studioError || !studioData) {
      return {
        studio: null,
        pendingBookings: [],
        todayBookings: [],
      };
    }

    // Load rooms
    const { data: roomsData } = await supabase
      .from("rooms")
      .select("*")
      .eq("studio_id", studioData.id);

    const roomIds = roomsData?.map((r) => r.id) || [];

    // Load pending bookings
    const { data: pendingData } = await supabase
      .from("bookings")
      .select(`
        *,
        room:rooms(name, studio_id, studio:studios(id, name, timezone)),
        renter:profiles!renter_id(email),
        booking_slots(*)
      `)
      .in("room_id", roomIds)
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    // Load today's bookings
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const { data: todayData } = await supabase
      .from("bookings")
      .select(`
        *,
        room:rooms(name, studio_id, studio:studios(id, name, timezone)),
        renter:profiles!renter_id(email),
        booking_slots(*)
      `)
      .in("room_id", roomIds)
      .eq("status", "approved")
      .gte("start_time", today.toISOString())
      .lt("start_time", tomorrow.toISOString())
      .order("start_time", { ascending: true });

    return {
      studio: studioData,
      pendingBookings: (pendingData || []) as BookingWithDetails[],
      todayBookings: (todayData || []) as BookingWithDetails[],
    };
  }

  async getBookingsData(userId: string): Promise<BookingsData> {
    // Load all studios owned by the user
    const { data: studiosData } = await supabase
      .from("studios")
      .select("*")
      .eq("owner_id", userId);

    if (!studiosData || studiosData.length === 0) {
      return {
        studios: [],
        rooms: [],
        bookings: [],
      };
    }

    // Get all rooms for the user's studios
    const studioIds = studiosData.map((s) => s.id);
    const { data: allRoomsData } = await supabase
      .from("rooms")
      .select("*, studio:studios(name)")
      .in("studio_id", studioIds)
      .order("name");

    // Get all bookings
    const { data: roomsData } = await supabase
      .from("rooms")
      .select("id")
      .in("studio_id", studioIds);

    let bookingQuery = supabase.from("bookings").select(`
      *,
      room:rooms(name, studio_id, studio:studios(id, name, timezone)),
      renter:profiles!renter_id(email),
      booking_slots(*)
    `);

    if (roomsData && roomsData.length > 0) {
      const roomIds = roomsData.map((r) => r.id);
      bookingQuery = bookingQuery.in("room_id", roomIds);
    }

    const { data: bookingsData } = await bookingQuery.order("created_at", { ascending: false });

    return {
      studios: studiosData,
      rooms: (allRoomsData || []) as Room[],
      bookings: (bookingsData || []) as BookingWithDetails[],
    };
  }

  async getBookingDetail(bookingId: string): Promise<BookingWithDetails | null> {
    const { data } = await supabase
      .from("bookings")
      .select(`
        *,
        room:rooms(name, studio:studios(id, name, timezone)),
        renter:profiles!renter_id(email),
        booking_slots(*)
      `)
      .eq("id", bookingId)
      .single();

    return data as BookingWithDetails | null;
  }

  async approveBooking(bookingId: string): Promise<boolean> {
    const session = await supabase.auth.getSession();
    const response = await fetch(`${API_URL}/bookings/${bookingId}/approve`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.data.session?.access_token}`,
      },
    });

    return response.ok;
  }

  async denyBooking(bookingId: string, reason?: string): Promise<boolean> {
    const session = await supabase.auth.getSession();
    const response = await fetch(`${API_URL}/bookings/${bookingId}/deny`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.data.session?.access_token}`,
      },
      body: reason ? JSON.stringify({ reason }) : undefined,
    });

    return response.ok;
  }

  async modifyBooking(
    bookingId: string,
    localSlots: Array<{ date: string; time: string }>
  ): Promise<BookingWithDetails | null> {
    const session = await supabase.auth.getSession();
    const response = await fetch(`${API_URL}/bookings/${bookingId}/modify`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.data.session?.access_token}`,
      },
      body: JSON.stringify({ local_slots: localSlots }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to modify rental");
    }

    return response.json();
  }

  async getAnalyticsData(userIdOrStudioId: string, dateRange: number = 90): Promise<AnalyticsData> {
    // Try loading studio by ID first, fallback to owner_id for backwards compat
    let studioData = null;
    const { data: byId } = await supabase
      .from("studios")
      .select("*")
      .eq("id", userIdOrStudioId)
      .maybeSingle();
    if (byId) {
      studioData = byId;
    } else {
      const { data: byOwner } = await supabase
        .from("studios")
        .select("*")
        .eq("owner_id", userIdOrStudioId)
        .single();
      studioData = byOwner;
    }

    if (!studioData) {
      return {
        studio: null,
        rooms: [],
        snapshots: [],
      };
    }

    // Load rooms
    const { data: roomsData } = await supabase
      .from("rooms")
      .select("*")
      .eq("studio_id", studioData.id)
      .order("created_at", { ascending: true });

    // Load snapshots
    const endDate = new Date();
    const startDate = subDays(endDate, dateRange);

    const { data: snapshotData } = await supabase
      .from("daily_availability_summary_snapshot")
      .select("*")
      .eq("studio_id", studioData.id)
      .gte("snapshot_date", format(startDate, "yyyy-MM-dd"))
      .lte("snapshot_date", format(endDate, "yyyy-MM-dd"))
      .order("snapshot_date", { ascending: true });

    // Load booking revenue data for each snapshot date
    const snapshotsWithRevenue = await Promise.all(
      (snapshotData || []).map(async (snapshot) => {
        let bookingQuery = supabase
          .from("bookings")
          .select("total_price, studio_payout_amount, status")
          .gte("start_time", `${snapshot.snapshot_date}T00:00:00`)
          .lt("start_time", `${snapshot.snapshot_date}T23:59:59`);

        if (snapshot.room_id) {
          bookingQuery = bookingQuery.eq("room_id", snapshot.room_id);
        }

        const { data: bookingData } = await bookingQuery;

        const approvedBookings = bookingData?.filter((b) => b.status === "approved") || [];
        const canceledBookings = bookingData?.filter((b) => b.status === "canceled") || [];

        // Calculate base revenue from approved bookings
        const baseRevenue = approvedBookings.reduce(
          (sum, booking) =>
            sum + (Number(booking.studio_payout_amount) || Number(booking.total_price) || 0),
          0
        );

        // Fetch balance adjustments applied/created on this date
        // Balance adjustments affect profit when they are created (refund happens)
        const { data: adjustmentData } = await supabase
          .from("studio_balance_adjustments")
          .select("amount")
          .eq("studio_id", studioData.id)
          .gte("created_at", `${snapshot.snapshot_date}T00:00:00`)
          .lt("created_at", `${snapshot.snapshot_date}T23:59:59`);

        // Sum up balance adjustments (negative amounts reduce profit)
        const adjustmentTotal = (adjustmentData || []).reduce(
          (sum, adj) => sum + Number(adj.amount),
          0
        );

        // Daily revenue = base bookings revenue + balance adjustments
        const dailyRevenue = baseRevenue + adjustmentTotal;

        const avgBookingValue = approvedBookings.length > 0 ? baseRevenue / approvedBookings.length : 0;
        const totalBookings = bookingData?.length || 0;
        const cancellationRate = totalBookings > 0 ? (canceledBookings.length / totalBookings) * 100 : 0;

        return {
          ...snapshot,
          daily_revenue: dailyRevenue,
          average_booking_value: avgBookingValue,
          cancellation_rate: cancellationRate,
          cancellations: canceledBookings.length,
          total_bookings: totalBookings,
        };
      })
    );

    return {
      studio: studioData,
      rooms: (roomsData || []) as Room[],
      snapshots: snapshotsWithRevenue as AvailabilitySnapshot[],
    };
  }

  async getStudio(userId: string): Promise<Studio | null> {
    const { data } = await supabase
      .from("studios")
      .select("*")
      .eq("owner_id", userId)
      .single();

    return data;
  }

  async getStudios(userId: string): Promise<Studio[]> {
    const { data } = await supabase
      .from("studios")
      .select("*")
      .eq("owner_id", userId);

    return data || [];
  }

  async getRooms(studioId: string): Promise<Room[]> {
    const { data } = await supabase
      .from("rooms")
      .select("*, room_rates(*)")
      .eq("studio_id", studioId)
      .order("name");

    return data || [];
  }

  async getRoom(roomId: string): Promise<Room | null> {
    const { data } = await supabase
      .from("rooms")
      .select("*")
      .eq("id", roomId)
      .single();

    return data;
  }

  async createRoom(room: Partial<Room>, studioId: string): Promise<Room> {
    const { data, error } = await supabase
      .from("rooms")
      .insert({
        ...room,
        studio_id: studioId,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updateRoom(roomId: string, updates: Partial<Room>): Promise<Room> {
    const { data, error } = await supabase
      .from("rooms")
      .update(updates)
      .eq("id", roomId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getPayouts(): Promise<PayoutData[]> {
    // This would need to be implemented with actual payout table
    // For now, return empty array
    return [];
  }

  async getPayoutDetail(): Promise<PayoutData | null> {
    // This would need to be implemented with actual payout table
    return null;
  }

  async updateStudioSettings(studioId: string, settings: Partial<Studio>): Promise<Studio> {
    const { data, error } = await supabase
      .from("studios")
      .update(settings)
      .eq("id", studioId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getCurrentUser(): Promise<Profile | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", user.id)
      .single();

    return data;
  }
}